import { type FC, useEffect, useState } from "react";

import { serverGenerateRecordingUrl, serverGenerateTranscriptUrl } from "api/api-server-actions";
import { LoadingScreen } from "components/LoadingScreen";
import { t } from "i18n/i18n";
import { Dialog } from "mds/components/Dialog";
import { useAppSelector } from "store/index";
import { recordingSessionSelectors } from "store/selectors";
import { toastErrorMessage } from "utils/alerts";
import VideoAsset from "worksheets/components/Field/VideoAsset";

type RecordingPlaybackDialogProps = {
  recordingSessionId: string | null;
  classSessionDate: string | null;
  onClose: () => void;
};

export const RecordingPlaybackDialog: FC<RecordingPlaybackDialogProps> = ({
  recordingSessionId,
  classSessionDate,
  onClose,
}) => {
  const [recordingUrl, setRecordingUrl] = useState<string>();
  const [transcriptUrl, setTranscriptUrl] = useState<string | null>();
  const [failed, setFailed] = useState(false);
  const transcriptRequested = useAppSelector(
    (state) =>
      recordingSessionSelectors.selectById(state, recordingSessionId)?.transcript_requested ===
      true,
  );

  useEffect(() => {
    // Clear on close so the next open does not paint the previous video or error before refetching.
    setRecordingUrl(undefined);
    setTranscriptUrl(undefined);
    setFailed(false);

    if (!recordingSessionId) return;

    let canceled = false;

    serverGenerateRecordingUrl(recordingSessionId, false)
      .then((nextRecordingUrl) => {
        if (canceled) return;
        setRecordingUrl(nextRecordingUrl);
      })
      .catch(() => {
        if (canceled) return;
        setFailed(true);
        toastErrorMessage(t("session_log_view.recording_load_failed"));
      });

    if (transcriptRequested) {
      serverGenerateTranscriptUrl(recordingSessionId)
        .then((nextTranscriptUrl) => {
          if (canceled) return;
          setTranscriptUrl(nextTranscriptUrl);
        })
        .catch(() => {
          // The API helper reports the failure. Captions are optional for playback.
        });
    } else {
      setTranscriptUrl(null);
    }

    return () => {
      canceled = true;
    };
  }, [recordingSessionId, transcriptRequested]);

  const tracks = transcriptUrl
    ? [
        {
          kind: "captions",
          label: t("session_log_view.auto_generated_captions"),
          src: transcriptUrl,
        },
      ]
    : undefined;

  return (
    <Dialog
      dialogPanelClassName="w-[min(960px,calc(100vw-4rem))]"
      open={recordingSessionId !== null}
      title={
        classSessionDate
          ? t("session_log_view.watch_recording_with_date", { date: classSessionDate })
          : t("session_log_view.watch_recording")
      }
      onClose={onClose}
    >
      <div className="flex min-h-48 items-center justify-center">
        {failed && <p>{t("session_log_view.recording_load_failed")}</p>}
        {!failed && !recordingUrl && <LoadingScreen />}
        {recordingUrl && (
          <VideoAsset
            className="max-h-[calc(100vh-10rem)] w-full bg-black object-contain"
            id={recordingSessionId ?? "recording"}
            tracks={tracks}
            url={recordingUrl}
          />
        )}
      </div>
    </Dialog>
  );
};
